import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaFilter, FaPlus, FaSlidersH, FaTable } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import Layout from "../../../components/Layout";
import { BoardColumn, Lead } from "../../../models/Lead";
import { ProspectStatus } from "../../../models/Lead";
import {
  deleteLead,
  getBoard,
  getStatuses,
  moveLead,
} from "../../../services/crmService";
import LeadBoard from "../components/LeadBoard";
import LeadFormModal from "../components/LeadFormModal";
import StatusManager from "../components/StatusManager";
import LeadTable from "../components/LeadTable";
import LeadColumnsModal from "../components/LeadColumnsModal";
import VisitCalendar, { CalendarVisit } from "../components/VisitCalendar";
import VisitDetailModal from "../components/VisitDetailModal";
import { defaultLeadColumns } from "../components/leadColumns";
import { crm } from "../components/crmTheme";

const Leads = () => {
  const auth = useAuthUser<any>();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [statuses, setStatuses] = useState<ProspectStatus[]>([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [editingLeadId, setEditingLeadId] = useState<string | undefined>();

  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef(null);
  const toast = useToast();

  const [tabIndex, setTabIndex] = useState(0);
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const stored = localStorage.getItem("crm_lead_columns");
    return stored ? JSON.parse(stored) : defaultLeadColumns;
  });
  const [selectedVisit, setSelectedVisit] = useState<CalendarVisit | null>(null);

  const load = async () => {
    setLoading(true);
    const [board, sts] = await Promise.all([getBoard(), getStatuses()]);
    setColumns(board);
    setStatuses(sts);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const allLeads = useMemo(
    () => columns.flatMap((col) => col.leads),
    [columns]
  );

  const allVisits = useMemo(
    () =>
      allLeads.flatMap((l) =>
        (l.visits ?? []).map((v) => ({ ...v, lead: l }))
      ),
    [allLeads]
  );

  const handleMove = async (lead: Lead, target: BoardColumn) => {
    const order = target.leads.length;
    setColumns((prev) =>
      prev.map((col) => {
        if (col.status.id === lead.prospectStatusId) {
          return { ...col, leads: col.leads.filter((l) => l.id !== lead.id) };
        }
        if (col.status.id === target.status.id) {
          return {
            ...col,
            leads: [
              ...col.leads,
              { ...lead, prospectStatusId: target.status.id, order },
            ],
          };
        }
        return col;
      })
    );
    await moveLead(lead.id!, {
      prospectStatusId: target.status.id ?? null,
      order,
    });
    load();
  };

  const openNew = () => {
    setEditingLeadId(undefined);
    setFormOpen(true);
  };

  const openEdit = (lead: Lead) => {
    setEditingLeadId(lead.id);
    setFormOpen(true);
  };

  const confirmDelete = async () => {
    if (!leadToDelete?.id) return;
    setDeleting(true);
    try {
      await deleteLead(leadToDelete.id);
      toast({
        title: "Lead excluído.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setLeadToDelete(null);
      setFormOpen(false);
      await load();
    } catch {
      toast({ title: "Erro ao excluir o lead.", status: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const updateSelectedColumns = (cols: string[]) => {
    setSelectedColumns(cols);
    localStorage.setItem("crm_lead_columns", JSON.stringify(cols));
  };

  return (
    <Box height={isMobile ? undefined : "100vh"} overflow="hidden">
      <Layout>
        <Box p="24px" bg={crm.pageBg} h="100%">
          {/* Page header */}
          <Flex
            justify="space-between"
            align="flex-start"
            mb="20px"
            flexWrap="wrap"
            gap="12px"
          >
            <Flex align="center" gap="12px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Box>
                <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">
                  Leads
                </Text>
                <Text color={crm.textMuted} fontSize="sm" mt="2px">
                  Gerencie leads, funil e visitas.
                </Text>
              </Box>
            </Flex>
            <Flex gap="10px">
              <Button
                leftIcon={<FaSlidersH />}
                variant="outline"
                borderColor={crm.border}
                color={crm.text}
                _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                borderRadius="10px"
                fontWeight="500"
                onClick={() => setStatusOpen(true)}
              >
                Gerenciar status
              </Button>
              <Button
                leftIcon={<FaPlus />}
                bg={crm.brand}
                color="white"
                _hover={{ bg: crm.brandDark }}
                borderRadius="10px"
                fontWeight="600"
                onClick={openNew}
              >
                Novo lead
              </Button>
            </Flex>
          </Flex>

          {loading ? (
            <Flex justify="center" align="center" minH="300px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : (
            <Tabs
              index={tabIndex}
              onChange={setTabIndex}
              variant="unstyled"
            >
              {/* Segmented pill tab control */}
              <TabList
                mb="20px"
                bg={crm.subtle}
                p="4px"
                borderRadius="10px"
                display="inline-flex"
                border={`1px solid ${crm.border}`}
              >
                {[
                  { icon: <FaTable />, label: "Tabela" },
                  { icon: <FaFilter />, label: "Funil" },
                  { icon: <FaCalendarAlt />, label: "Calendário" },
                ].map(({ icon, label }, i) => (
                  <Tab
                    key={label}
                    borderRadius="8px"
                    px="16px"
                    py="6px"
                    fontSize="sm"
                    fontWeight={tabIndex === i ? "600" : "400"}
                    color={tabIndex === i ? crm.brand : crm.textMuted}
                    bg={tabIndex === i ? crm.surface : "transparent"}
                    boxShadow={tabIndex === i ? crm.shadowSm : "none"}
                    _hover={{ color: tabIndex === i ? crm.brand : crm.text }}
                    transition="all .15s"
                  >
                    <Flex align="center" gap="6px">
                      {icon}
                      {label}
                    </Flex>
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                <TabPanel p={0}>
                  <LeadTable
                    leads={allLeads}
                    selectedColumns={selectedColumns}
                    onEdit={openEdit}
                    onDelete={(lead) => setLeadToDelete(lead)}
                    onOpenColumnsModal={() => setColumnsModalOpen(true)}
                  />
                </TabPanel>
                <TabPanel p={0}>
                  <LeadBoard
                    columns={columns}
                    onMove={handleMove}
                    onLeadClick={openEdit}
                    onLeadDelete={(lead) => setLeadToDelete(lead)}
                  />
                </TabPanel>
                <TabPanel p={0}>
                  <VisitCalendar
                    visits={allVisits}
                    onVisitClick={(v) => setSelectedVisit(v)}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </Box>

        <LeadFormModal
          isOpen={formOpen}
          onClose={() => setFormOpen(false)}
          onSaved={load}
          statuses={statuses}
          leadId={editingLeadId}
          onManageStatus={() => setStatusOpen(true)}
          onDelete={(lead) => setLeadToDelete(lead)}
        />
        <StatusManager
          isOpen={statusOpen}
          onClose={() => setStatusOpen(false)}
          onChanged={load}
        />
        <LeadColumnsModal
          isOpen={columnsModalOpen}
          onClose={() => setColumnsModalOpen(false)}
          selectedColumns={selectedColumns}
          setSelectedColumns={updateSelectedColumns}
        />
        <VisitDetailModal
          visit={selectedVisit}
          isOpen={!!selectedVisit}
          onClose={() => setSelectedVisit(null)}
          onOpenLead={openEdit}
        />

        <AlertDialog
          isOpen={!!leadToDelete}
          leastDestructiveRef={cancelRef}
          onClose={() => setLeadToDelete(null)}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Excluir lead
              </AlertDialogHeader>
              <AlertDialogBody>
                Tem certeza que deseja excluir o lead{" "}
                <b>{leadToDelete?.name || "Sem nome"}</b>? Esta ação registra
                quem excluiu e quando, e o remove do funil.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} variant="ghost" onClick={() => setLeadToDelete(null)}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="red"
                  onClick={confirmDelete}
                  isLoading={deleting}
                  ml={3}
                  borderRadius="10px"
                >
                  Excluir
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Layout>
    </Box>
  );
};

export default Leads;
